// The tenant's ATS plus its configured external sources, as the slim header's source dropdown
// lists them (`shortlist-extension-alerts.component.ng1.ts:231, 344`).
//
// A pure module for the same reason as `dropdownGroups`: this is a transcription of two
// constant maps and one list-building rule, and every part of it has a way of being subtly
// wrong that renders fine.

// ⚠️ IMPORTED, NOT REDECLARED. `SocialHostType` is a WIRE contract — the server sends
// 'CVLibrary' and the port must send it back — so it belongs with the client that speaks to
// the server, and `packages/ui` already depends on `@woo-ext/api`. A second copy here would
// typecheck, render identically, and drift the first time upstream adds a host.
import { SocialHost } from '@woo-ext/api'

export { SocialHost }

/**
 * `SocialHostDisplayName` (shortlist.constants.ng1.ts:317).
 *
 * ⚠️ ONLY CVLIBRARY DIFFERS FROM ITS VALUE — 'CVLibrary' displays as 'CV Library'. Every other
 * entry maps a string to itself, which makes the whole map look redundant and makes "just use
 * the value" look safe. It is not: that one entry is the reason the map exists.
 */
export const SOCIAL_HOST_DISPLAY_NAME: Record<SocialHost, string> = {
  [SocialHost.Linkedin]: 'Linkedin',
  [SocialHost.Indeed]: 'Indeed',
  [SocialHost.Ats]: 'ATS',
  [SocialHost.CvLibrary]: 'CV Library',
  [SocialHost.Monster]: 'Monster',
  [SocialHost.Reed]: 'Reed',
}

/**
 * `ATSAndExternalSourceTypeLogo` (shortlist.constants.ng1.ts:326).
 *
 * ⚠️ PARTIAL ON PURPOSE. Only three of the possible hosts have a logo, and the template's
 * `ng-if="!!…icon"` is what keeps the others text-only. Filling the gaps with a placeholder
 * would add an image where production shows none. Keyed by ATS NAME as well as social host,
 * because the ATS row looks its icon up by `window.careero.atsName`.
 */
export const SOURCE_LOGO: Readonly<Record<string, string>> = {
  Bullhorn: '/static/assets/img/extension/externalSource/bullhorn-es-logo.png',
  [SocialHost.Monster]: '/static/assets/img/extension/externalSource/monster-es-logo.jpg',
  [SocialHost.CvLibrary]: '/static/assets/img/extension/externalSource/cvl-es-logo.svg',
}

export interface SourceOption {
  /**
   * The source's `SocialHostType`, or `null` for the tenant's own ATS.
   *
   * ⚠️ `null` IS THE ATS AND IT IS LOAD-BEARING. Upstream's map gives the ATS entry
   * `type: null` while keying it under `'ATS'`, and `onSourceClick` compares
   * `currentExternalSource == sourceInfo.type` to decide whether the click is a no-op. With
   * `'ATS'` as the type, selecting the ATS from a non-ATS source would compare unequal in the
   * wrong direction and the tick would land on the wrong row.
   */
  type: SocialHost | null
  name: string
  icon?: string
}

/**
 * The dropdown's rows: the ATS first, then each configured external source in the order the
 * server returned them (component:352).
 *
 * `atsName` comes from the bootstrap (`window.careero.atsName`) and keys the logo map. The row's
 * NAME is the ATS label (`getAtsLabel($window)`, 5cfd3ef899) — the same string unless the tenant
 * overrides it, which is exactly when the two must not be confused: a label is never a logo key.
 */
export function sourceOptions(
  atsName: string,
  configuredHosts: readonly SocialHost[],
  atsLabel: string = atsName,
): SourceOption[] {
  const ats: SourceOption = {
    type: null,
    name: atsLabel,
    ...(SOURCE_LOGO[atsName] === undefined ? {} : { icon: SOURCE_LOGO[atsName] }),
  }
  return [
    ats,
    ...configuredHosts.map((host) => ({
      type: host,
      name: SOCIAL_HOST_DISPLAY_NAME[host],
      ...(SOURCE_LOGO[host] === undefined ? {} : { icon: SOURCE_LOGO[host] }),
    })),
  ]
}

/**
 * Which row the trigger shows.
 *
 * The template keys the info map with `currentExternalSource || 'ATS'`, so an unset source
 * falls back to the ATS row — matching `type: null`, not a row whose type is `'ATS'`.
 */
export function currentSourceOption(options: readonly SourceOption[], current?: SocialHost | null): SourceOption | undefined {
  if (current === undefined || current === null) return options.find((option) => option.type === null)
  return options.find((option) => option.type === current)
}
